import { useState, useEffect, useCallback, useRef } from 'react';
import type { OpenAITool } from '../types';
import { fetchTools } from '../api';

interface UseToolsOptions {
  apiUrl: string;
  apiKey?: string;
  enabled?: boolean;
}

export function useTools({ apiUrl, apiKey, enabled = true }: UseToolsOptions) {
  const [tools, setTools] = useState<OpenAITool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setTools([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedTools = await fetchTools({ apiUrl, apiKey });
      setTools(fetchedTools);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setTools([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, apiKey, enabled]);

  // Fetch tools on mount and when enabled changes
  useEffect(() => {
    if (enabled && !fetchedRef.current) {
      fetchedRef.current = true;
      refresh();
    }
  }, [enabled, refresh]);

  // Reset fetched flag when disabled
  useEffect(() => {
    if (!enabled) {
      fetchedRef.current = false;
      setTools([]);
    }
  }, [enabled]);

  return {
    tools,
    isLoading,
    error,
    refresh,
    hasTools: tools.length > 0,
  };
}
